import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { Room } from '@chore-project/shared';

interface RoomInput {
  name: string;
  description?: string;
}

/** Fetch rooms for multiple households and merge into a single array. */
export function useAllRooms(householdIds: string[]) {
  const results = useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['rooms', id],
      queryFn: async () => {
        const { data } = await apiClient.get<Room[]>(`/households/${id}/rooms`);
        return data;
      },
      enabled: !!id,
    })),
  });
  const rooms = results.flatMap((r) => r.data ?? []);
  const isLoading = results.some((r) => r.isLoading);
  return { data: rooms, isLoading };
}

export function useRooms(householdId: string) {
  return useQuery<Room[]>({
    queryKey: ['rooms', householdId],
    queryFn: async () => {
      const { data } = await apiClient.get<Room[]>(`/households/${householdId}/rooms`);
      return data;
    },
    enabled: !!householdId,
  });
}

export function useCreateRoom(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<Room, Error, RoomInput>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.post<Room>(`/households/${householdId}/rooms`, dto);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rooms', householdId] }),
  });
}

export function useUpdateRoom(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<Room, Error, { roomId: string } & Partial<RoomInput>>({
    mutationFn: async ({ roomId, ...dto }) => {
      const { data } = await apiClient.patch<Room>(
        `/households/${householdId}/rooms/${roomId}`,
        dto,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rooms', householdId] }),
  });
}

export function useDeleteRoom(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (roomId) => {
      await apiClient.delete(`/households/${householdId}/rooms/${roomId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rooms', householdId] }),
  });
}
